import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import {
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Modal,
  Image,
  ActionIcon,
  TextInput,
  Checkbox,
} from '@mantine/core';
import {
  IconUpload,
  IconChevronLeft,
  IconChevronRight,
  IconTrash,
  IconPencil,
} from '@tabler/icons-react';
import type { RepositoryCode } from '@ayocore/platform/common/types/repositories.generated';
import type { UploadedFile } from '../../schemas/uploaded_file.schema';
import type { FileCategory } from '../../schemas/file-category.schema';
import type { EntityAttachment } from '../../server/repositories/UploadedFileLnkRepository';
import {
  useUploadedFileLnkRepository,
  useFileCategoryRepository,
  useUploadedFileRepository,
} from '../rpc';
import { FileUploadModal } from './FileUploadModal';
import { useFileDrop } from './useFileDrop';
import type { ImageProcessingOptions } from '@ayocore/platform/client/utils';
import { resolveFileUrl } from '../fileUrl';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { useConfirm } from '@ayocore/platform/client/hooks/useConfirm';
import logger from '@ayocore/platform/common/helpers/logger';

interface EntityFilesProps {
  // — Источник —
  /** UUID сущности (lead.uuid, deal.uuid и т.д.) */
  entityUuid: string;
  /** Тип сущности — typesafe ключ из REPOSITORIES */
  entityType: RepositoryCode;
  /** Директория для загрузки файлов (leads, deals, partners и т.д.) */
  directory: string;

  // — Отображение —
  /**
   * Вид отображения:
   *  - 'list' (по умолчанию) — список всех файлов;
   *  - 'gallery' — сетка миниатюр ТОЛЬКО изображений (image/*) с лайтбоксом.
   */
  view?: 'list' | 'gallery';
  /**
   * Заголовок блока; по умолчанию «Фото» (галерея) / «Прикрепленные файлы».
   * `null` — скрыть заголовок (когда блок уже подписан снаружи — нет дубля).
   */
  title?: string | null;
  /**
   * Свои элементы в правом верхнем углу, ПОСЛЕ кнопки «Загрузить» (слот для
   * действий вызывающего — напр. «Сформировать архив»).
   */
  headerActions?: ReactNode;

  // — Категории (filemanager, defineFileCategory) —
  /**
   * Закрепить категорию по slug: показываются только файлы этой категории, при
   * загрузке она проставляется молча (без селектора и чипсов). Пример: вкладки
   * «Фото»/«Акты» инспекции. Взаимоисключимо с showCategories.
   */
  fixedCategorySlug?: string;
  /**
   * Вместе с fixedCategorySlug: показывать и файлы БЕЗ категории (legacy-загрузки
   * до ввода категорий). Новые загрузки всё равно получают закреплённую категорию.
   */
  includeUncategorized?: boolean;
  /**
   * Белый список категорий (slug): показываются чипсы-фильтр и файлы ТОЛЬКО этих
   * категорий (+ без категории), при загрузке предлагаются только они. Не задан —
   * категории не показываются (плоский список всех файлов). Пример: «Документы
   * недели» перечисляют свои категории, скрывая «Сформированный архив».
   */
  showCategories?: string[];

  // — Права —
  /** Разрешить загрузку файлов (кнопка «Загрузить»). По умолчанию true. */
  allowUpload?: boolean;
  /** Разрешить удаление (отвязку) файлов. По умолчанию true. */
  allowDelete?: boolean;
  /**
   * Заменить стандартную отвязку своей операцией удаления. Один вызов на весь
   * выбранный набор (миниатюра, просмотрщик и массовое действие).
   * Promise разрешается только при успехе; ошибка показывается пользователю,
   * fallback на detach НЕТ. Подтверждение и обновление списка держит компонент.
   * Без обработчика прежняя семантика вложений не меняется.
   */
  onDeleteFiles?: (fileIds: number[]) => Promise<void>;
  /**
   * Разрешить переименование уже загруженных файлов (карандаш → правка
   * original_name; физический файл не двигается). По умолчанию false.
   */
  allowRename?: boolean;

  // — Загрузка —
  /** Разрешить выбор нескольких файлов за раз. */
  multiple?: boolean;
  /** Фильтр типов в диалоге выбора (напр. 'application/pdf,image/*'); галерея по умолчанию image/*. */
  accept?: string;
  /**
   * Переименование файла перед загрузкой (проброс в FileUploadModal): возвращает
   * целевое имя. Не задан / вернул прежнее имя — файл грузится как есть.
   */
  renameOnUpload?: (file: File, index: number) => string;
  /**
   * Пережатие фото на сервере при загрузке (проброс в FileUploadModal): ресайз по
   * макс. стороне `width` + JPEG `quality`. Оригинал не сохраняется. Не задан —
   * файлы грузятся без обработки.
   */
  imageProcessingOptions?: ImageProcessingOptions;

  // — События —
  /** Колбэк после успешной загрузки файлов (список уже перезагружен). */
  onUploaded?: () => void;
}

/** Форматирование размера файла в человекочитаемый вид */
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Файл — изображение (для режима галереи). */
function isImage(file: UploadedFile): boolean {
  return typeof file.mime_type === 'string' && file.mime_type.startsWith('image/');
}

/** Активный фильтр по категории: все / без категории / конкретная категория. */
type CategoryFilter = 'all' | 'none' | number;

/** Сторона миниатюры в галерее (px). */
const THUMB = 110;

/**
 * EntityFiles — универсальный компонент для отображения файлов, привязанных к сущности.
 *
 * Загружает файлы через filemanager::UploadedFileLnkRepository.getEntityAttachments().
 * Два вида (проп `view`): список файлов или галерея фото (сетка миниатюр + лайтбокс).
 * Без обёртки — вызывающий оборачивает в ViewPanel/Card по месту.
 */
export function EntityFiles({
  entityUuid,
  entityType,
  directory,
  view = 'list',
  title,
  headerActions,
  fixedCategorySlug,
  includeUncategorized = false,
  showCategories,
  allowUpload = true,
  allowDelete = true,
  onDeleteFiles,
  allowRename = false,
  multiple = false,
  accept,
  renameOnUpload,
  imageProcessingOptions,
  onUploaded,
}: EntityFilesProps) {
  const lnkRepository = useUploadedFileLnkRepository();
  const categoryRepository = useFileCategoryRepository();
  const fileRepository = useUploadedFileRepository();
  const confirm = useConfirm();
  const [files, setFiles] = useState<EntityAttachment[]>([]);
  /** Категории точки привязки (пусто = механизм категорий не используется). */
  const [categories, setCategories] = useState<FileCategory[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  /** Файлы, брошенные на блок (drag-n-drop) — открывают модалку предзаполненной. */
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  /** Индекс открытого в лайтбоксе фото (галерея); null — закрыт. */
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  /** id файла под курсором (для показа корзинки) и id удаляемого сейчас. */
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const deletingRef = useRef(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** Отмеченные галочками файлы (id) и флаг идущего массового удаления. */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  /** Файл в процессе переименования (открыт модал) + редактируемое имя + id сохранения. */
  const [renameTarget, setRenameTarget] = useState<UploadedFile | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renamingId, setRenamingId] = useState<number | null>(null);

  const loadFiles = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const data = await lnkRepository.getEntityAttachments(entityUuid, entityType);
        setFiles(data || []);
        // Список пересобран — прежние отметки могли указывать на уже отвязанные файлы.
        setSelectedIds(new Set());
      } catch (error) {
        logger.error('[EntityFiles] Ошибка загрузки файлов:', error);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [entityUuid, entityType, lnkRepository]
  );

  useEffect(() => {
    if (entityUuid) {
      loadFiles();
    }
  }, [entityUuid, loadFiles]);

  // Категории сущности: есть зарегистрированные (defineFileCategory) — включается
  // фильтр и обязательный выбор категории при загрузке; нет — прежнее поведение.
  useEffect(() => {
    let active = true;
    const loadCategories = async () => {
      try {
        const data = await categoryRepository
          .filterByEntityType(entityType)
          .filterActive()
          .orderBy('sort_order', 'asc')
          .fetch();
        if (active) setCategories(data || []);
      } catch (error) {
        logger.error('[EntityFiles] Ошибка загрузки категорий:', error);
      }
    };
    void loadCategories();
    return () => {
      active = false;
    };
  }, [entityType, categoryRepository]);

  /** Отражаем подтверждённое удаление даже при ошибке последующего fetch. */
  const removeFilesFromView = (fileIds: number[]) => {
    const removed = new Set(fileIds);
    const remainingPhotos = photos.filter(photo => !removed.has(photo.id));
    setFiles(current => current.filter(file => !removed.has(file.id)));
    setSelectedIds(current => new Set([...current].filter(id => !removed.has(id))));
    setLightboxIndex(current => {
      if (current === null) return null;
      if (remainingPhotos.length === 0) return null;
      const activeId = photos[current]?.id;
      const retainedIndex = remainingPhotos.findIndex(photo => photo.id === activeId);
      if (retainedIndex >= 0) return retainedIndex;
      // Текущий кадр удалён: следующий на его месте либо последний оставшийся.
      const before = photos.slice(0, current).filter(photo => !removed.has(photo.id)).length;
      return Math.min(before, remainingPhotos.length - 1);
    });
  };

  /** Своя операция либо штатная отвязка — без неявного переключения при отказе. */
  const handleDelete = async (file: UploadedFile) => {
    if (!allowDelete || deletingRef.current) return;
    // Фиксируем цель уже на время подтверждения, не только RPC.
    deletingRef.current = true;
    setDeletingId(file.id);
    setDeleteError(null);
    try {
      const question = onDeleteFiles
        ? `Удалить файл «${file.original_name}»?`
        : `Удалить «${file.original_name}» из вложений?`;
      const ok = await confirm(question, {
        title: 'Удаление файла',
        confirmLabel: 'Удалить',
        danger: true,
      });
      if (!ok) return;
      if (onDeleteFiles) await onDeleteFiles([file.id]);
      else await lnkRepository.detach(file.id, entityUuid, entityType);
      removeFilesFromView([file.id]);
      await loadFiles(true);
    } catch (error) {
      logger.error('[EntityFiles] Ошибка удаления файла:', error);
      setDeleteError(error instanceof Error ? error.message : 'Не удалось удалить файл');
    } finally {
      deletingRef.current = false;
      setDeletingId(null);
    }
  };

  /** Отметить/снять один файл. */
  const toggleSelected = (fileId: number) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (!next.delete(fileId)) next.add(fileId);
      return next;
    });

  /**
   * Один вопрос и один вызов на весь батч: своя операция либо detachMany.
   */
  const handleDeleteSelected = async (targetIds: number[]) => {
    if (!allowDelete || targetIds.length === 0 || deletingRef.current) return;
    deletingRef.current = true;
    setBulkDeleting(true);
    setDeleteError(null);
    try {
      const ok = await confirm(`Удалить выбранные файлы (${targetIds.length})?`, {
        title: 'Удаление файлов',
        confirmLabel: 'Удалить',
        danger: true,
      });
      if (!ok) return;
      if (onDeleteFiles) await onDeleteFiles(targetIds);
      else await lnkRepository.detachMany(targetIds, entityUuid, entityType);
      removeFilesFromView(targetIds);
      await loadFiles(true);
    } catch (error) {
      logger.error('[EntityFiles] Ошибка массового удаления файлов:', error);
      setDeleteError(error instanceof Error ? error.message : 'Не удалось удалить файлы');
    } finally {
      deletingRef.current = false;
      setBulkDeleting(false);
    }
  };

  /** Открыть переименование: префилл текущим отображаемым именем. */
  const handleRename = (file: UploadedFile) => {
    setRenameValue(file.original_name ?? '');
    setRenameTarget(file);
  };

  /** Сохранить новое имя: меняем только original_name (физический файл не трогаем). */
  const submitRename = async () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    if (!name) return;
    try {
      setRenamingId(renameTarget.id);
      await fileRepository.update(renameTarget.id, { original_name: name });
      setRenameTarget(null);
      await loadFiles();
    } catch (error) {
      logger.error('[EntityFiles] Ошибка переименования файла:', error);
    } finally {
      setRenamingId(null);
    }
  };

  const isGallery = view === 'gallery';
  // title === null → заголовок скрыт (блок подписан снаружи); undefined → дефолт.
  const heading = title === null ? null : (title ?? (isGallery ? 'Фото' : 'Прикрепленные файлы'));
  /** Закреплённая категория (по slug); null — слаг не найден среди категорий типа. */
  const fixedCategory = fixedCategorySlug
    ? (categories.find(c => c.slug === fixedCategorySlug) ?? null)
    : null;
  const isPinned = fixedCategorySlug != null;
  /** Категории для чипсов-фильтра (whitelist showCategories); пусто — фильтр скрыт. */
  const shownCategories =
    !isPinned && showCategories?.length
      ? categories.filter(c => showCategories.includes(c.slug))
      : [];
  const shownCategoryIds = new Set(shownCategories.map(c => c.id));
  /** В whitelist-режиме показываем только файлы выбранных категорий (+ без категории). */
  const scopedFiles =
    !isPinned && showCategories?.length
      ? files.filter(f => f.category_id == null || shownCategoryIds.has(f.category_id))
      : files;
  /** Файлы вида: галерея видит только изображения. */
  const baseFiles = isGallery ? scopedFiles.filter(isImage) : scopedFiles;
  const matchesPinned = (file: EntityAttachment) =>
    (fixedCategory != null && file.category_id === fixedCategory.id) ||
    (includeUncategorized && file.category_id == null);
  const matchesFilter = (file: EntityAttachment) =>
    categoryFilter === 'all' ||
    (categoryFilter === 'none' ? file.category_id == null : file.category_id === categoryFilter);
  const visibleFiles = isPinned
    ? baseFiles.filter(matchesPinned)
    : shownCategories.length > 0
      ? baseFiles.filter(matchesFilter)
      : baseFiles;
  const photos = isGallery ? visibleFiles : [];
  const visibleCount = visibleFiles.length;
  const uncategorizedCount = baseFiles.filter(f => f.category_id == null).length;
  // Выделение всегда сужаем до видимых файлов: под фильтром категории (и в галерее,
  // где не-изображений нет) удалиться должно только то, что пользователь реально видит.
  const selectedVisibleIds = visibleFiles.flatMap(file =>
    selectedIds.has(file.id) ? [file.id] : []
  );
  const selectedCount = selectedVisibleIds.length;
  const allSelected = visibleCount > 0 && selectedCount === visibleCount;
  /** Галочки показываем только там, где удаление разрешено и есть что выделять. */
  const selectable = allowDelete && visibleCount > 0;

  /** Отметить все видимые / снять все. */
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(visibleFiles.map(file => file.id)));

  /** Смена фильтра категории меняет набор видимого — отметки сбрасываем. */
  const changeCategoryFilter = (value: CategoryFilter) => {
    setCategoryFilter(value);
    setSelectedIds(new Set());
  };
  const activePhoto = lightboxIndex !== null ? (photos[lightboxIndex] ?? null) : null;
  // Z-index поверх дравера/модалки, из которой открыли (старые Modal вне ModalHost —
  // иначе уходят под дравер). Хуки до раннего return ниже — порядок вызова стабилен.
  const lightboxZIndex = useSurfaceZIndex(activePhoto !== null);
  const renameZIndex = useSurfaceZIndex(renameTarget !== null);

  // Фильтр типов для дропа/загрузки — как у модалки (галерея по умолчанию image/*).
  const uploadAccept = accept ?? (isGallery ? 'image/*' : undefined);
  // Дроп файлов на весь блок → открыть модалку загрузки уже с этими файлами.
  // Пока модалка открыта — блок дроп НЕ ловит: иначе дроп в Dropzone модалки
  // всплывает через React-портал сюда и обрабатывается повторно (дубли файлов).
  const { dragActive, dropHandlers } = useFileDrop({
    onFiles: dropped => {
      setDroppedFiles(dropped);
      setShowUploadModal(true);
    },
    accept: uploadAccept,
    multiple,
    disabled: !allowUpload || showUploadModal,
  });

  if (loading) {
    return (
      <>
        {heading !== null && (
          <Text size='xs' fw={600}>
            {heading}
          </Text>
        )}
        <Text size='sm' c='dimmed'>
          Загрузка…
        </Text>
      </>
    );
  }

  const showLightbox = (delta: number) => {
    if (deletingRef.current) return;
    setDeleteError(null);
    setLightboxIndex(current => {
      if (current === null || photos.length === 0) return current;
      return (current + delta + photos.length) % photos.length;
    });
  };

  return (
    <div {...dropHandlers} style={{ position: 'relative' }}>
      <Group justify='space-between'>
        {/* span-заглушка слева: без заголовка space-between держит кнопки справа */}
        {heading !== null ? (
          <Text size='xs' fw={600}>
            {heading}
          </Text>
        ) : (
          <span />
        )}
        <Group gap='xs'>
          {selectable && (
            <Checkbox
              size='xs'
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              onChange={toggleAll}
              aria-label={allSelected ? 'Снять выделение' : 'Выделить все'}
            />
          )}
          {visibleCount > 0 && (
            <Badge size='sm' variant='light'>
              {visibleCount}
            </Badge>
          )}
          {allowUpload && (
            <Button
              size='xs'
              variant='light'
              leftSection={<IconUpload size={14} />}
              onClick={() => setShowUploadModal(true)}
            >
              Загрузить
            </Button>
          )}
          {headerActions}
        </Group>
      </Group>

      {deleteError && activePhoto === null && (
        <Text role='alert' c='red' size='sm'>
          {deleteError}
        </Text>
      )}

      {/* Панель массовых действий — только когда есть отмеченные. */}
      {selectedCount > 0 && (
        <Group gap='xs' mt='xs'>
          <Text size='xs' c='dimmed'>
            Выбрано: {selectedCount}
          </Text>
          <Button
            size='compact-xs'
            color='red'
            variant='light'
            leftSection={<IconTrash size={14} />}
            loading={bulkDeleting}
            disabled={deletingId !== null}
            onClick={() => void handleDeleteSelected(selectedVisibleIds)}
          >
            Удалить
          </Button>
          <Button size='compact-xs' variant='subtle' onClick={() => setSelectedIds(new Set())}>
            Снять
          </Button>
        </Group>
      )}

      {shownCategories.length > 0 && !isPinned && (
        <Group gap={6} mt='xs'>
          <Button
            size='compact-xs'
            variant={categoryFilter === 'all' ? 'filled' : 'light'}
            onClick={() => changeCategoryFilter('all')}
          >
            Все · {baseFiles.length}
          </Button>
          {shownCategories.map(category => (
            <Button
              key={category.id}
              size='compact-xs'
              variant={categoryFilter === category.id ? 'filled' : 'light'}
              onClick={() => changeCategoryFilter(category.id)}
            >
              {category.name} · {baseFiles.filter(f => f.category_id === category.id).length}
            </Button>
          ))}
          {uncategorizedCount > 0 && (
            <Button
              size='compact-xs'
              variant={categoryFilter === 'none' ? 'filled' : 'light'}
              onClick={() => changeCategoryFilter('none')}
            >
              Без категории · {uncategorizedCount}
            </Button>
          )}
        </Group>
      )}

      {isGallery ? (
        photos.length === 0 ? (
          <Text size='sm' c='dimmed'>
            Нет фотографий
          </Text>
        ) : (
          <Group gap='xs' wrap='wrap' mt='xs'>
            {photos.map((photo, index) => {
              const isSelected = selectedIds.has(photo.id);
              return (
                <div
                  key={photo.id}
                  style={{ position: 'relative', width: THUMB, height: THUMB }}
                  onMouseEnter={() => setHoveredId(photo.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <Image
                    src={resolveFileUrl(photo.public_url)}
                    alt={photo.original_name ?? undefined}
                    title={photo.original_name ?? undefined}
                    w={THUMB}
                    h={THUMB}
                    radius='sm'
                    fit='cover'
                    style={{
                      cursor: 'pointer',
                      // Отмеченное фото обводим — на сетке миниатюр одной галочки в углу мало.
                      border: isSelected
                        ? '2px solid var(--mantine-color-blue-6)'
                        : '1px solid var(--mantine-color-gray-3)',
                    }}
                    onClick={() => {
                      if (deletingRef.current) return;
                      setDeleteError(null);
                      setLightboxIndex(index);
                    }}
                  />
                  {/* Галочка видна у отмеченных всегда, у остальных — по наведению. */}
                  {allowDelete && (
                    <Checkbox
                      size='xs'
                      checked={isSelected}
                      onChange={() => toggleSelected(photo.id)}
                      aria-label={`Выбрать «${photo.original_name ?? photo.id}»`}
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        opacity: isSelected || hoveredId === photo.id ? 1 : 0,
                        transition: 'opacity 120ms',
                      }}
                    />
                  )}
                  <Group
                    gap={4}
                    wrap='nowrap'
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      opacity: hoveredId === photo.id ? 1 : 0,
                      transition: 'opacity 120ms',
                    }}
                  >
                    {allowRename && (
                      <ActionIcon
                        variant='filled'
                        color='gray'
                        size='sm'
                        radius='sm'
                        onClick={e => {
                          e.stopPropagation();
                          handleRename(photo);
                        }}
                        aria-label='Переименовать фото'
                      >
                        <IconPencil size={14} />
                      </ActionIcon>
                    )}
                    {allowDelete && (
                      <ActionIcon
                        variant='filled'
                        color='red'
                        size='sm'
                        radius='sm'
                        loading={deletingId === photo.id}
                        disabled={bulkDeleting || (deletingId !== null && deletingId !== photo.id)}
                        onClick={e => {
                          e.stopPropagation();
                          void handleDelete(photo);
                        }}
                        aria-label='Удалить фото'
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                </div>
              );
            })}
          </Group>
        )
      ) : visibleFiles.length === 0 ? (
        <Text size='sm' c='dimmed'>
          Нет прикрепленных файлов
        </Text>
      ) : (
        <Stack gap={2}>
          {visibleFiles.map(file => (
            <Group
              key={file.id}
              justify='space-between'
              wrap='nowrap'
              gap='xs'
              px={6}
              py={3}
              style={{
                borderRadius: 4,
                backgroundColor:
                  hoveredId === file.id ? 'var(--mantine-color-gray-1)' : 'transparent',
                transition: 'background-color 120ms',
              }}
              onMouseEnter={() => setHoveredId(file.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <Group gap='xs' align='center' wrap='nowrap' style={{ minWidth: 0 }}>
                {allowDelete && (
                  <Checkbox
                    size='xs'
                    checked={selectedIds.has(file.id)}
                    onChange={() => toggleSelected(file.id)}
                    aria-label={`Выбрать «${file.original_name ?? file.id}»`}
                    style={{ flexShrink: 0 }}
                  />
                )}
                <a
                  href={resolveFileUrl(file.public_url)}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ textDecoration: 'none' }}
                >
                  <Text size='sm' truncate>
                    {file.original_name}
                  </Text>
                </a>
                <Text size='xs' c='dimmed' style={{ flexShrink: 0 }}>
                  {formatFileSize(file.file_size)}
                </Text>
              </Group>
              <Group
                gap={2}
                wrap='nowrap'
                style={{
                  flexShrink: 0,
                  opacity: hoveredId === file.id ? 1 : 0,
                  transition: 'opacity 120ms',
                }}
              >
                {allowRename && (
                  <ActionIcon
                    variant='subtle'
                    color='gray'
                    size='sm'
                    onClick={() => handleRename(file)}
                    aria-label='Переименовать файл'
                  >
                    <IconPencil size={14} />
                  </ActionIcon>
                )}
                {allowDelete && (
                  <ActionIcon
                    variant='subtle'
                    color='red'
                    size='sm'
                    loading={deletingId === file.id}
                    disabled={bulkDeleting || (deletingId !== null && deletingId !== file.id)}
                    onClick={() => void handleDelete(file)}
                    aria-label='Удалить файл'
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                )}
              </Group>
            </Group>
          ))}
        </Stack>
      )}

      {/* Лайтбокс (галерея) */}
      <Modal
        zIndex={lightboxZIndex}
        opened={activePhoto !== null}
        onClose={() => setLightboxIndex(null)}
        size='90%'
        centered
        title={activePhoto?.original_name}
      >
        {activePhoto && (
          <Stack gap='xs'>
            {allowDelete && (
              <Group justify='flex-end'>
                <Button
                  color='red'
                  variant='light'
                  size='compact-xs'
                  leftSection={<IconTrash size={16} />}
                  loading={deletingId !== null}
                  disabled={bulkDeleting}
                  onClick={() => void handleDelete(activePhoto)}
                >
                  Удалить
                </Button>
              </Group>
            )}
            {deleteError && (
              <Text role='alert' c='red' size='sm'>
                {deleteError}
              </Text>
            )}
            <Group justify='space-between' align='center' wrap='nowrap' gap='xs'>
              <ActionIcon
                variant='subtle'
                size='lg'
                disabled={photos.length < 2 || deletingId !== null || bulkDeleting}
                onClick={() => showLightbox(-1)}
                aria-label='Предыдущее фото'
              >
                <IconChevronLeft size={20} />
              </ActionIcon>
              <Image
                key={activePhoto.id}
                src={resolveFileUrl(activePhoto.public_url)}
                alt={activePhoto.original_name ?? undefined}
                fit='contain'
                mah='80vh'
                style={{ flex: 1, minWidth: 0 }}
              />
              <ActionIcon
                variant='subtle'
                size='lg'
                disabled={photos.length < 2 || deletingId !== null || bulkDeleting}
                onClick={() => showLightbox(1)}
                aria-label='Следующее фото'
              >
                <IconChevronRight size={20} />
              </ActionIcon>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Переименование (меняет только отображаемое имя original_name). */}
      <Modal
        zIndex={renameZIndex}
        opened={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        title='Переименовать файл'
        size='md'
      >
        <Stack gap='sm'>
          <TextInput
            label='Имя файла'
            value={renameValue}
            onChange={e => setRenameValue(e.currentTarget.value)}
            data-autofocus
            onKeyDown={e => {
              if (e.key === 'Enter') void submitRename();
            }}
          />
          <Group justify='flex-end'>
            <Button variant='default' onClick={() => setRenameTarget(null)}>
              Отмена
            </Button>
            <Button
              onClick={() => void submitRename()}
              loading={renamingId !== null}
              disabled={!renameValue.trim()}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Оверлей-подсказка при перетаскивании файлов на блок. */}
      {dragActive && allowUpload && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            border: '2px dashed var(--mantine-color-blue-5)',
            backgroundColor: 'var(--mantine-color-blue-0)',
            opacity: 0.92,
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <Text size='sm' fw={600} c='blue'>
            Отпустите файлы для загрузки
          </Text>
        </div>
      )}

      <FileUploadModal
        opened={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setDroppedFiles([]);
        }}
        entityUuid={entityUuid}
        entityType={entityType}
        directory={directory}
        multiple={multiple}
        accept={uploadAccept}
        categories={isPinned ? [] : shownCategories}
        defaultCategoryId={typeof categoryFilter === 'number' ? categoryFilter : null}
        fixedCategoryId={fixedCategory?.id}
        initialFiles={droppedFiles}
        renameOnUpload={renameOnUpload}
        imageProcessingOptions={imageProcessingOptions}
        onUploaded={() => {
          void loadFiles();
          onUploaded?.();
        }}
      />
    </div>
  );
}
